import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme
} from "@mui/material";
import MenuList from "./layout/MenuList";
import BaseMenuList from "./layout/BaseMenuList";
import { useTranslation } from "react-i18next";
import PhoneIcon from "@mui/icons-material/Phone";
// import EmailIcon from "@mui/icons-material/Email";
import PublicIcon from "@mui/icons-material/Public";

type LayoutProps = {
  background?: "gradient" | "white" | "mammogram";
};

export default function Layout({ background = "white" }: LayoutProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const { pathname } = location;
  const hasStep = new URLSearchParams(location.search).has("step");
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const user = localStorage.getItem("user");

  const pagesWithTitle: Record<string, string> = {
    "/": "Home",
    "/register": "THERMOGRAM",
    "/agree": "Agree",
    "/sign-agree": "Sign Agree",
    "/identity": "Identity",
    "/questionnaire": "Questionnaire",
    "/appointment": "Appointment",
    "/payment": "Payment",
    "/report": "Report",
    "/history": "History",
    "/booking": "Appointment Booking",
    "/profile-settings": "Profile & Setting",
    "/forget-password": "Forget Password",
    "/forget-password/:token": "Reset Password",
    "/promotions": "Promotions",
    "/appointment-history": "Appointment History",
    "/packages": "Appointment Packages",
    "/mammogram/result": "Line Mamogram Result",
  };

  const pageWithoutMenu: Record<string, string> = {
    "/sign-agree": "Sign Agree",
    "/mammogram": "Line Mamogram Check",
    "/mammogram/result": "Line Mamogram Result",
  };

  // ฟังก์ชันหา title ที่รองรับ dynamic route ทุกอันที่มี /:[key]
  function getPageTitle(pathname: string) {
    if (pagesWithTitle[pathname]) return pagesWithTitle[pathname];
    for (const key in pagesWithTitle) {
      if (key.includes("/:")) {
        const pattern = new RegExp("^" + key.replace(/:[^/]+/g, "[^/]+") + "$");
        if (pattern.test(pathname)) {
          return pagesWithTitle[key];
        }
      }
    }
    return "";
  }

  const pageTitle = getPageTitle(pathname);
  const isShowDrawer = Boolean(!pageWithoutMenu[pathname])

  const handleLogout = () => {
    const savedLang = localStorage.getItem("i18nextLng");
    const department = localStorage.getItem("department");
    if (department === "CMT"){
      // const token_event = localStorage.getItem("token");
      // localStorage.clear();
      localStorage.removeItem("registerData");
      localStorage.removeItem("consent");
      localStorage.removeItem("patient_id");
      localStorage.removeItem("user");
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("bookingData");

      // localStorage.setItem("token", token_event);
      // localStorage.setItem("department", "CMT");
      // case: after register
    }else{
      localStorage.clear();
    }

    if (savedLang) {
      void i18n.changeLanguage(savedLang);
    }
    navigate("/login");
  };

  const handleMenuNavigate = (path: string, slug: undefined | string) => {
    if (slug === "logout") {
      handleLogout();
    } else if (slug === "lang") {
      toggleLang();
    } else if (slug === "contact-us") {
      setContactOpen(true);
    } else if (slug === "winmed") {
      window.open("https://www.winmed.com/th/home", "_blank");
    } else {
      navigate(path);
    }
    setMenuOpen(false);
  };

  const toggleLang = () => {
    const newLang = i18n.language === "th" ? "en" : "th";
    void i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setContactOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const menu = {
    links: [
      { label: t("menu.dashboard"), path: "/report" },
      // { label: t("menu.bookAppointment"), path: "/appointment" },
        { label: t("menu.booking"), path: "/booking" },
      { label: t("menu.history"), path: "/history" },
      { label: "Winmed Health", path: "https://www.winmed.com/th/home", slug: 'winmed' },
      { label: t("menu.yourProfile"), path: "/profile-settings" },
      { label: t("menu.privacyNotice"), path: "/agree" },
      { label: t("menu.promotions"), path: "/promotions" },
      { label: t("menu.vouchers"), path: "/vouchers" },
    ],
    baseMenu: [
      ...(pathname === "/login"
        ? [{ label: "ไทย/EN", path: "#change-lang", slug: 'lang' }]
        : [{ label: t("menu.contactUs"), path: "#contact-us", slug: 'contact-us' }]),
      { label: t("menu.logout"), path: "/login", slug: 'logout' },
    ],
  };

  const styleBackground = (background: string): string => {
    switch (background) {
      case "gradient":
        if (hasStep) {
          return "linear-gradient(180deg, #f5a39c, #efb699, #FFF1E6)";
        } else {
          return "linear-gradient(135deg, #bfa2ca, #f5a39c, #efb699)";
        }
      case "white":
        return "linear-gradient(180deg, #FFF1E6 70%, #efb699)";
      case "mammogram":
        return "linear-gradient(180deg, #7378ab 50%, #c5bde0)";
      default:
        return "linear-gradient(180deg, #FFF1E6 70%, #efb699)";
    }
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center w-screen relative ${
        background === "gradient" ? "text-white" : "text-[#3F4E8C]"
      }`}
      style={{
        background: styleBackground(background),
      }}
    >
      <header
        className="w-full px-4 flex justify-between items-center pb-1 pt-5"
        style={{ maxWidth: "500px" }}
      >
        <div className="h-10 w-full">
          {pageTitle ? (
            <img
              src={`/images/thermogram-${
                background === "gradient" ? "white" : "gradient"
              }.png`}
              alt="Logo"
              className="h-15 cursor-pointer -ml-3"
              onClick={() => navigate("/")}
            />
          ) : (
            <div className="h-10"></div>
          )}
        </div>
        {isShowDrawer && <div className="flex-shrink-0">
          <IconButton
            onClick={() => setMenuOpen(true)}
            sx={{
              padding: "0",
            }}
          >
            <img
              src={`/images/icons/menu-${
                background === "gradient" ? "white" : "gradient"
              }.webp`}
              alt="Menu"
              className="h-10 hover:opacity-80 transition-opacity duration-200 cursor-pointer"
            />
          </IconButton>
        </div>}
      </header>

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background:
              "linear-gradient(135deg, rgba(239, 182, 153, 0.50), rgba(245, 163, 156, 0.50), rgba(191, 162, 202, 0.50))",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)", // Safari support
            border: "none",
            boxShadow: "none",
            color: "#3f4e8c",
            height: "100vh",
            display: "flex",
            justifyContent: "space-between",
            px: 3,
            pb: 10,
          },
        }}
        ModalProps={{
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
            },
          },
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "100%",
            top: 20,
            right: 16,
            display: "flex",
            justifyContent: "flex-end",
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={() => setMenuOpen(false)}
            sx={{
              padding: "0",
            }}
          >
            <img
              src={`/images/icons/menu-white.webp`}
              alt="Close Menu"
              className="h-10 hover:opacity-80 transition-opacity duration-200 cursor-pointer"
            />
          </IconButton>
        </div>
        {user ? (
          <MenuList
            links={menu.links}
            onNavigate={handleMenuNavigate}
            activePath={pathname}
          />
        ) : (
          <div></div>
        )}
        <BaseMenuList
          links={menu.baseMenu}
          onNavigate={handleMenuNavigate}
          user={user}
        />
      </Drawer>

      <Dialog open={contactOpen} onClose={() => setContactOpen(false)}>
        <DialogTitle sx={{ color: theme.palette.secondary.dark}} >Contact Us</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 mt-2">
            {/*<div className="flex items-center gap-2">*/}
            {/*  <EmailIcon color="primary" />*/}
            {/*  <a*/}
            {/*    href="mailto:support@example.com"*/}
            {/*    className="text-blue-600 underline"*/}
            {/*    style={{ wordBreak: "break-all" , color: theme.palette.secondary.dark}}*/}
            {/*  >*/}
            {/*    support@example.com*/}
            {/*  </a>*/}
            {/*</div>*/}
            <div className="flex items-center gap-2">
              <PhoneIcon color="primary" />
              <a href="tel:085-555-1818" style={{color: theme.palette.secondary.dark}} className="text-blue-600 underline">
                085-555-1818
              </a>
            </div>
            <div className="flex items-center gap-2">
              <PublicIcon color="primary" />
              <a href="https://lin.ee/ZqbDlni" style={{color: theme.palette.secondary.dark}} target="_blank" className="text-blue-600 underline">
                LINE: Winmed Health
              </a>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <main className="flex-1 w-full z-0" style={{ maxWidth: "500px" }}>
        <Outlet />
      </main>
    </div>
  );
}
